import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", err: "" });
  const attempts = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", err: "Missing session id" }); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "paid") {
          await refresh();
          setState({ status: "paid", err: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", err: "" });
          return;
        }
        attempts.current += 1;
        if (attempts.current > 10) {
          setState({ status: "timeout", err: "" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", err: formatApiError(e) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="h-6 w-6 text-gold-500 animate-spin mx-auto" />
          <h1 className="mt-4 font-serif text-3xl text-olive-700">Bekræfter din betaling…</h1>
          <p className="mt-2 text-brown-600 text-sm">Dette tager normalt et par sekunder.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div className="mx-auto h-16 w-16 rounded-full bg-olive-100 border border-olive-500/30 flex items-center justify-center">
            <Check className="h-7 w-7 text-olive-600" />
          </div>
          <h1 className="mt-6 font-serif text-4xl text-olive-700">Velkommen, værdsatte medlem</h1>
          <p className="mt-3 text-brown-600">Dit medlemskab er aktivt. Du kan nu låse op for fulde profiler og starte private samtaler.</p>
          <div className="mt-8 flex justify-center gap-3">
            <button onClick={() => navigate("/matches")} className="btn-primary" data-testid="success-goto-matches">Se dine forslag</button>
            <button onClick={() => navigate("/chat")} className="btn-secondary" data-testid="success-goto-chat">Åbn samtaler</button>
          </div>
        </>
      )}
      {state.status === "expired" && <FailState title="Session udløbet" msg="Din betalingssession udløb før gennemførelsen." />}
      {state.status === "timeout" && <FailState title="Behandler stadig" msg="Vi bekræfter stadig. Genindlæs siden om et øjeblik." />}
      {state.status === "error" && <FailState title="Noget gik galt" msg={state.err} />}
      {state.status !== "paid" && state.status !== "polling" && (
        <Link to="/pricing" className="btn-secondary mt-6 inline-flex">Tilbage til priser</Link>
      )}
    </div>
  );
}

function FailState({ title, msg }) {
  return (
    <>
      <h1 className="font-serif text-3xl text-olive-700">{title}</h1>
      <p className="mt-3 text-brown-600">{msg}</p>
    </>
  );
}
